import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation, Link } from "react-router-dom";
import { Button, Card, Table, Alert } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faEdit, faTrash, faPlus, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { colors, constants, generateRoutePath, CLIENT_TYPES } from "utils";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { request } from "hooks/useOkoApi/useOkoApi";

const PageContainer = styled.div`
  max-width: 1200px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.nightlyWoods700};
  text-decoration: none;
  margin-bottom: 16px;
  &:hover {
    color: ${colors.nightlyWoods900};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const InfoCard = styled(Card)`
  margin-bottom: 24px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const InfoItem = styled.div`
  label {
    font-size: 12px;
    color: ${colors.nightlyWoods600};
    text-transform: uppercase;
    display: block;
    margin-bottom: 4px;
  }
  span {
    font-size: 14px;
    color: ${colors.nightlyWoods900};
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0 16px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const StyledTable = styled(Table)`
  background: white;
  border-radius: 8px;
  overflow: hidden;

  th {
    background: ${colors.milkGlass400};
    border-bottom: none;
  }

  tbody tr {
    cursor: pointer;
    &:hover {
      background: ${colors.milkGlass200};
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

export const ClientDetail = () => {
  const { id } = useParams();
  const { authUserData, loading: userLoading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const [client, setClient] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [hasFetched, setHasFetched] = useState(false);

  const backToListUrl = generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME) + (location.state?.listQuery || "");

  useEffect(() => {
    if (userLoading) return;

    if (!authUserData || authUserData.isAnonymous) {
      history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
      return;
    }

    if (!hasFetched) {
      fetchClientData();
    }
  }, [authUserData, userLoading, hasFetched, id]);

  const fetchClientData = async () => {
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const [clientResponse, schoolsResponse] = await Promise.all([
        request(`admin/clients/${id}`, { method: "GET" }),
        request(`admin/schools?client_id=${id}`, { method: "GET" }),
      ]);
      setClient(clientResponse);
      setSchools(schoolsResponse.schools || []);
    } catch (err) {
      console.error("Failed to fetch client:", err);
      setError("Failed to load client. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    history.push(generateRoutePath(constants.CLIENT_ADMIN_EDIT_ROUTE_NAME, { id }));
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await request(`admin/clients/${id}`, { method: "DELETE" });
      history.push(generateRoutePath(constants.CLIENT_ADMIN_ROUTE_NAME));
    } catch (err) {
      console.error("Failed to delete client:", err);
      setError("Failed to delete client. Please try again.");
    }
  };

  const handleAddSchool = () => {
    history.push(generateRoutePath(constants.SCHOOL_ADMIN_NEW_ROUTE_NAME, { clientId: id }));
  };

  const handleSchoolClick = (schoolId) => {
    history.push(generateRoutePath(constants.SCHOOL_ADMIN_DETAIL_ROUTE_NAME, { id: schoolId }));
  };

  const formatClientType = (type) => {
    if (!type) return "-";
    const found = CLIENT_TYPES.find(t => t.value.toLowerCase() === type.toLowerCase());
    return found ? found.label : type;
  };

  if (userLoading || loading) {
    return (
      <LoadingContainer>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      </LoadingContainer>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!client) {
    return <Alert variant="warning">Client not found.</Alert>;
  }

  return (
    <PageContainer>
      <BackLink to={backToListUrl}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Clients
      </BackLink>

      <PageHeader>
        <PageTitle>{client.name}</PageTitle>
        <ActionButtons>
          <Button variant="outline-secondary" onClick={handleEdit}>
            <FontAwesomeIcon icon={faEdit} /> Edit
          </Button>
          <Button variant="outline-danger" onClick={handleDelete}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </Button>
        </ActionButtons>
      </PageHeader>

      <InfoCard>
        <Card.Body>
          <InfoGrid>
            <InfoItem>
              <label>Type</label>
              <span>{formatClientType(client.client_type)}</span>
            </InfoItem>
            <InfoItem>
              <label>State</label>
              <span>{client.state || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>NCES LEA ID</label>
              <span>{client.nces_lea_id || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>HubSpot Company ID</label>
              <span>{client.hubspot_company_id || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Billing Email</label>
              <span>{client.billing_email || "-"}</span>
            </InfoItem>
            <InfoItem>
              <label>Billing Contact</label>
              <span>{client.billing_contact_name || "-"}</span>
            </InfoItem>
          </InfoGrid>
          {client.notes && (
            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 12, color: colors.nightlyWoods600, textTransform: "uppercase" }}>Notes</label>
              <p style={{ margin: 0 }}>{client.notes}</p>
            </div>
          )}
        </Card.Body>
      </InfoCard>

      <SectionHeader>
        <SectionTitle>Schools ({schools.length})</SectionTitle>
        <Button
          onClick={handleAddSchool}
          style={{ backgroundColor: colors.nightlyWoods500, borderColor: colors.nightlyWoods500 }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add School
        </Button>
      </SectionHeader>

      <StyledTable hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>City</th>
            <th>State</th>
            <th>NCES ID</th>
          </tr>
        </thead>
        <tbody>
          {schools.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ textAlign: "center", padding: "40px" }}>
                No schools found for this client.
              </td>
            </tr>
          ) : (
            schools.map((school) => (
              <tr key={school.id} onClick={() => handleSchoolClick(school.id)}>
                <td><strong>{school.name}</strong></td>
                <td>{school.city || "-"}</td>
                <td>{school.state || "-"}</td>
                <td>{school.nces_school_id || "-"}</td>
              </tr>
            ))
          )}
        </tbody>
      </StyledTable>
    </PageContainer>
  );
};
